import React, { FC, useEffect, useMemo, useState } from "react";
import "../area-table.scss";
import { Column } from "../../../../types/column";
import StringCell from "./string-cell/string-cell";
import DeleteCell from "./delete-cell/delete-cell";
import { CSSTransition, TransitionGroup } from "react-transition-group";

interface Props<T> {
  columns: Column[];
  rows: T[];
  handleDelete: (row: T) => void;
}

interface AbstractItem {
  id: number;
}

export default function Table<T extends AbstractItem>({
  columns,
  rows,
  handleDelete,
}: Props<T>) {
  const columnsGrid = useMemo(() => {
    const clms = columns.filter((item) => !item.hideHead);
    const repeat = clms
      .map((item) => item.width || "minmax(70px, 1fr)")
      .join(" ");

    return {
      gridTemplateColumns: `${repeat}`,
    };
  }, [columns]);

  const [hoverIndex, setHoverIndex] = useState<boolean | number>(false);
  const [hoverRow, setHoverRow] = useState<boolean | number>(false);

  const setHover = (rowId: number | boolean, index: number | boolean) => {
    setHoverIndex(index);
    setHoverRow(rowId);
  };

  const DynamicComponent = (
    column: Column,
  ): React.FC | React.Component | any => {
    const type = column.type || "";

    switch (type) {
      case "delete":
        return DeleteCell;
      default:
        return StringCell;
    }
  };

  return (
    <div className="table">
      <div
        className={`table-row table-row_header ${hoverIndex === 0 ? "table-row_header_hide-border" : ""}`}
        style={columnsGrid}
      >
        {columns
          .filter((column) => !column.hideHead)
          .map((column) => (
            <div
              key={column.id}
              className="table-row-cell table-row-cell_header"
            >
              <span>{column.title}</span>
            </div>
          ))}
      </div>
      <div className="table-body">
        <TransitionGroup>
          {rows.map((row, index) => (
            <CSSTransition
              key={row.id}
              classNames="fade"
              timeout={150}
              unmountOnExit
            >
              <div
                style={columnsGrid}
                className={`table-row ${hoverIndex === index + 1 ? "table-row_hide-border" : ""} ${hoverRow === row.id ? "table-row_active" : ""}`}
                onMouseEnter={() => setHover(row.id, index)}
                onMouseLeave={() => setHover(false, false)}
              >
                {columns.map((column) => {
                  const Component = DynamicComponent(column);

                  return (
                    <div
                      className="table-row-cell"
                      key={column.id}
                      style={column.style || {}}
                    >
                      <Component
                        row={row}
                        column={column}
                        index={index}
                        hover={hoverRow}
                        handleDelete={handleDelete}
                      />
                    </div>
                  );
                })}
              </div>
            </CSSTransition>
          ))}
        </TransitionGroup>
      </div>
    </div>
  );
}
